/**
 * Utilities for surfacing backend API error messages to the user.
 */

interface ApiErrorShape {
  response?: {
    data?: {
      // Specific, human-readable failure reason from the Go server's
      // util.HTTPSendError (e.g. "a user can belong to only one team in an organization").
      error?: unknown;
      // Static, generic label the handler passes (e.g. "Failed to update team").
      message?: unknown;
    };
  };
  message?: unknown;
}

const firstNonBlankString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return undefined;
};

/**
 * Extract the most specific, human-readable message from an API error.
 *
 * The Go server's `util.HTTPSendError` returns bodies shaped like
 * `{ "error": "<specific reason>", "message": "<generic label>", "code": <status> }`.
 * `error` holds the concrete failure reason while `message` is only a static, generic
 * label — so reading `message` alone hides the real reason from the user. We therefore
 * prefer `response.data.error`, then fall back to `response.data.message`, then the
 * Axios-level error message, then a caller-supplied default.
 */
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const err = (error ?? {}) as ApiErrorShape;
  const data = err.response?.data;

  return firstNonBlankString(data?.error, data?.message, err.message) ?? fallback;
};
